import { queryOptions } from '@tanstack/react-query'

import { fetchLoginOptions, type LoginOption, type RedirectingLoginOption } from './api'

/**
 * The ways in that this instance offers.
 *
 * From the backend rather than from configuration here, because only the backend
 * knows whether a provider row actually exists to sign in against -- a
 * misconfigured instance should say so, not show a button that leads to an error
 * page.
 */
export function loginOptionsQuery() {
  return queryOptions({
    queryKey: ['auth', 'login-options'] as const,
    staleTime: Infinity,
    retry: false,
    // The sign-in page reports an unreachable backend itself, in more detail than
    // a toast could, so the global handler has nothing to add.
    meta: { skipGlobalErrorHandler: true },
    queryFn: (): Promise<Record<string, LoginOption>> => fetchLoginOptions(),
  })
}

/**
 * The one way in, or null.
 *
 * Filtered rather than simply taking the first entry: a development instance also
 * offers the password seam, which carries no `redirect_url`, and following it
 * produces a link to nowhere. Null then means what the page says it means -- this
 * instance has no way to sign in through a browser.
 *
 * Written as a selector even though there is exactly one provider in this fork, so
 * adding a second one later is a change to the page rather than to every caller.
 */
export function theOnlyLoginOption(
  options: Record<string, LoginOption> | undefined,
): RedirectingLoginOption | null {
  if (options === undefined) return null

  const offerable = Object.values(options).filter(
    (option): option is RedirectingLoginOption =>
      typeof option.redirect_url === 'string' && typeof option.name === 'string',
  )
  return offerable[0] ?? null
}
